import type { PipeInterface } from './usePipe'

// Provider-agnostic record of payments-api session fetches, so consumers that
// don't need to know which checkout provider handled it (GTM, saved-card
// reuse) don't have to ask. payments-api — not the frontend — decides which
// provider actually charges a given customer (e.g. a saved card can route to
// either), so a caller-side "is this Primer or Yuno" branch is often the
// wrong question to begin with.
//
// Every caller of usePaymentSession — usePrimer, useYuno, SavedCard — writes
// here via `recordLatestPaymentSession` right after a successful fetch, and
// via `recordCompletedPaymentSession` right after an order actually
// completes (not the same moment: a fetched session can still be abandoned).
// Deliberately a loose bag rather than a typed union: Primer's PaymentSession
// and Yuno's YunoCheckoutSession use different field names for the
// SDK-specific credential (`clientToken` vs `checkoutSession`), and each
// composable also stashes its own cache-bookkeeping (`paymentDataKey`)
// alongside the raw response.
export const PAYMENT_SESSION_KEY = 'var.global.paymentSession'

export type PaymentSessionRecord = Record<string, unknown>

export type PaymentSessionState = {
  // Most recent fetch, successful or not yet known to have completed an
  // order — overwritten by every fetchPaymentSession call regardless of
  // provider. A visitor can abandon a fetched session, so this is NOT
  // reliably "the session behind a completed order" — see `completed` below.
  latest?: PaymentSessionRecord
  completed?: {
    // The session behind the FIRST order this visitor completed in this pipe
    // session. Set once, never overwritten.
    initial?: PaymentSessionRecord
    // The session behind the MOST RECENT order this visitor completed (e.g.
    // a post-purchase upsell charged via SavedCard, after the main purchase).
    current?: PaymentSessionRecord
  }
}

// Writes `session` into `latest`. Call this right after any successful
// `fetchPaymentSession()`, regardless of whether the checkout goes on to
// complete.
export function recordLatestPaymentSession(
  pipe: PipeInterface,
  session: PaymentSessionRecord,
): void {
  const existing =
    (pipe.getData(PAYMENT_SESSION_KEY) as PaymentSessionState | null | undefined) ?? {}
  pipe.setData(PAYMENT_SESSION_KEY, { ...existing, latest: session })
}

// Writes `session` into `completed.current` (always) and `completed.initial`
// (only the first time). Call this from the exact success path of a checkout
// (Primer's onCheckoutComplete, Yuno's yunoPaymentResult success branch,
// SavedCard's instantlyCharged branch) using the session that ACTUALLY
// completed — not by re-reading `latest`, which may have been overwritten by
// an unrelated fetch (another component, a retry) in the meantime.
export function recordCompletedPaymentSession(
  pipe: PipeInterface,
  session: PaymentSessionRecord,
): void {
  const existing =
    (pipe.getData(PAYMENT_SESSION_KEY) as PaymentSessionState | null | undefined) ?? {}
  const completed = existing.completed ?? {}
  pipe.setData(PAYMENT_SESSION_KEY, {
    ...existing,
    completed: {
      initial: completed.initial ?? session,
      current: session,
    },
  })
}

// Single point of truth for "what's the payment-session id on this record" —
// every reader (GTM, etc.) should go through this instead of indexing
// `paymentSessionId` directly, so a payments-api contract change is a
// one-line fix here instead of a hunt across every consumer.
// `trackingEvent.messageId` (Primer's PaymentSession — see paymentSession.ts)
// carries the same id as the top-level `paymentSessionId` field and is
// preferred: it's the field payments-api's own analytics pipeline treats as
// canonical. Yuno's session type has no `trackingEvent` at all, so the
// top-level field is kept as the fallback for records that lack one.
export function getPaymentSessionId(
  record: PaymentSessionRecord | null | undefined,
): string | null {
  if (!record) return null

  const trackingEvent = record['trackingEvent'] as { messageId?: unknown } | undefined
  const messageId = trackingEvent?.messageId
  if (typeof messageId === 'string' && messageId) return messageId

  const id = record['paymentSessionId']
  return typeof id === 'string' && id ? id : null
}
